var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Card, CardSection, Flag, Skeleton, Styler, TypographyHeading, TypographyBody, InfoItem, LocationIcon, Button, Divider, useIntersection, } from '@honeycomb/ui-core';
import { parseBoolean, useGlobalContext, useWishlistCard } from '@honeycomb/data';
import { WishlistRemoveButton } from './WishlistRemoveButton';
import { HotelCardImageCarousel, HotelRatings, useHotelFlag, getHotelLocation, getEstabLandingPageUrl, getHotelDetailsUrl, } from '../hotel';
const MAX_IMAGES = 5;
const IMAGE_HEIGHT = 240;
const IMAGE_CROP_DIMS = {
    xs: {
        w: 420,
        h: IMAGE_HEIGHT,
    },
    s: {
        w: 620,
        h: IMAGE_HEIGHT,
    },
    m: {
        w: 490,
        h: IMAGE_HEIGHT,
    },
    l: {
        w: 430,
        h: IMAGE_HEIGHT,
    },
};
export function WishlistHotelCard(_a) {
    var _b;
    var { estabId, onReviewsDialogTriggerClick } = _a, other = __rest(_a, ["estabId", "onReviewsDialogTriggerClick"]);
    const { resourceStrings: { HC_WISHLIST_CARD_CTA_LABEL }, configSettings: { ProductCacheEnabled, EnableWishlistLandingPageRedirection }, } = useGlobalContext();
    const { handleRef } = useIntersection();
    const [observed, setObserved] = React.useState(false);
    const { data: staticData } = useWishlistCard(estabId || '', [], !estabId || !observed);
    const { tags, numberOfPhotos, name: hotelName, geography } = (_b = staticData === null || staticData === void 0 ? void 0 : staticData.getEstabById) !== null && _b !== void 0 ? _b : {};
    const imageCount = Math.min(numberOfPhotos !== null && numberOfPhotos !== void 0 ? numberOfPhotos : 0, MAX_IMAGES);
    const shouldRedirectToLandingPage = parseBoolean(EnableWishlistLandingPageRedirection);
    const redirectUrl = shouldRedirectToLandingPage
        ? getEstabLandingPageUrl(estabId || '')
        : getHotelDetailsUrl(false, estabId || '');
    const hotelPageHref = estabId ? redirectUrl : null;
    const hotelLocation = geography && getHotelLocation(Object.assign(Object.assign({}, geography), { includeCountry: parseBoolean(ProductCacheEnabled) }));
    const hotelFlag = useHotelFlag(false, tags || undefined);
    const [waitingForRemoval, setWaitingForRemoval] = React.useState(false);
    // If we have no estabId or have not yet retrieved the hotel data then
    // we will display a skeleton loading state.
    const loading = !estabId || !(staticData === null || staticData === void 0 ? void 0 : staticData.getEstabById);
    return (React.createElement(Styler, { pos: "relative" },
        React.createElement(Card, Object.assign({ h: "100%", ref: handleRef === null || handleRef === void 0 ? void 0 : handleRef(() => {
                setObserved(true);
            }), orientation: "portrait", 
            // The 'flag' is positioned over the image in the top left corner
            adornments: hotelFlag ? (React.createElement(Flag, { ml: -2, top: "20px", color: hotelFlag.color, "data-id": `flag_${hotelFlag.label.toLowerCase().replace(' ', '_')}` }, hotelFlag.label)) : undefined, w: "100%", padding: 4, "data-key": estabId, dimmed: waitingForRemoval }, other),
            React.createElement(CardSection, { h: IMAGE_HEIGHT, p: 0, pos: "relative", zIndex: 1 },
                React.createElement(React.Fragment, null, loading ? (React.createElement(Skeleton, { h: "100%" })) : (React.createElement(HotelCardImageCarousel, { estabId: estabId || '', cropDims: IMAGE_CROP_DIMS, hotelName: hotelName || '', imageCount: imageCount, ImageLinkProps: { href: hotelPageHref } },
                    React.createElement(WishlistRemoveButton, { estabId: estabId, onRemove: () => setWaitingForRemoval(true), onRemoveError: () => setWaitingForRemoval(false) }))))),
            React.createElement(CardSection, { pt: { xs: 4 }, flexGrow: 1 },
                loading ? (React.createElement(TypographyHeading, { component: "div", size: "s" },
                    React.createElement(Skeleton, { variant: "text" }))) : (React.createElement(TypographyHeading, { component: "h4", size: "s" }, hotelName)),
                React.createElement(Styler, { mt: 2 }, loading ? (React.createElement(TypographyBody, { component: "div", w: "60%", size: "s" },
                    React.createElement(Skeleton, { variant: "text" }))) : (React.createElement(InfoItem, { icon: React.createElement(LocationIcon, null), "data-id": "hotel_location", "data-key": estabId }, hotelLocation))),
                React.createElement(HotelRatings, { hotel: staticData === null || staticData === void 0 ? void 0 : staticData.getEstabById, onDialogTriggerClick: (origin) => (staticData === null || staticData === void 0 ? void 0 : staticData.getEstabById) && (onReviewsDialogTriggerClick === null || onReviewsDialogTriggerClick === void 0 ? void 0 : onReviewsDialogTriggerClick(origin, staticData.getEstabById)), mt: 3 })),
            React.createElement(CardSection, { pt: 0 },
                React.createElement(Divider, { mb: 4 }),
                loading ? (React.createElement(Skeleton, { radius: 10, maw: "100%", my: 3 },
                    React.createElement(Button, { size: "m", fullWidth: true, miw: 140 }))) : (React.createElement(Button, { color: "callToAction", fullWidth: true, size: "l", href: hotelPageHref || undefined, "data-id": "hotel_view_more_button", "data-key": estabId }, HC_WISHLIST_CARD_CTA_LABEL))))));
}
